import { useMemo, useState } from 'react';
import {
	Alert,
	Box,
	CircularProgress,
	Divider,
	Paper,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	useMediaQuery,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { schoolApi } from '../../services';
import { SchoolStatsByIdGroupedResponseSchema } from '../../services/schoolApi';
import type { z } from 'zod';

const SECTION_GAP = 2;
const MOBILE_BREAKPOINT = 'sm';
const MODE_YEAR = 'year';
const MODE_MAJOR = 'major';
const MODE_METHOD = 'method';
const EMPTY_TEXT = 'Chưa có dữ liệu';
const UNKNOWN_LABEL = 'Chưa xác định';
const FACULTY_TOTAL_LABEL = 'Số lượng';
const FACULTY_TOTAL_PADDING_X = 1;
const FACULTY_TOTAL_PADDING_Y = 0.25;
const FACULTY_TOTAL_BORDER_RADIUS = 1;
const FACULTY_TOTAL_BG_COLOR = 'primary.light';
const FACULTY_TOTAL_BORDER_COLOR = 'primary.main';
const FACULTY_TOTAL_TEXT_COLOR = 'white';
const FACULTY_TOTAL_TEXT_WEIGHT = 600;

type StatsMode = typeof MODE_YEAR | typeof MODE_MAJOR | typeof MODE_METHOD;

type SchoolStatsByIdGrouped = z.infer<typeof SchoolStatsByIdGroupedResponseSchema>;
type SchoolStatsApiResponse = Awaited<ReturnType<typeof schoolApi.getSchoolStatsById>>;

type YearGroup<T> = {
	year: number | null;
	items: T[];
};

type MajorGroupItem = {
	majorName: string;
	count: number;
};

type FacultyMajorGroup = {
	facultyName: string;
	majors: MajorGroupItem[];
	totalCount: number;
};

type YearMajorGroup = {
	year: number | null;
	faculties: FacultyMajorGroup[];
};

type MethodGroupItem = {
	methodName: string;
	count: number;
};

interface SchoolStatsPanelProps {
	schoolId: string;
	open: boolean;
	selectedFaculty?: string | null;
}

const getDisplayYear = (year: number | null) => (year === null ? UNKNOWN_LABEL : String(year));

const buildMajorGroups = (data: SchoolStatsByIdGrouped['majorGroupStats']): YearMajorGroup[] => {
	const yearMap = new Map<number | null, Map<string, MajorGroupItem[]>>();

	data.forEach((item: SchoolStatsByIdGrouped['majorGroupStats'][number]) => {
		const year = item.year ?? null;
		const facultyName = item.tenKhoa || UNKNOWN_LABEL;
		const majorName = item.tenNganh || UNKNOWN_LABEL;
		const facultyMap = yearMap.get(year) ?? new Map<string, MajorGroupItem[]>();
		const majors = facultyMap.get(facultyName) ?? [];
		majors.push({
			majorName,
			count: item.count,
		});
		facultyMap.set(facultyName, majors);
		yearMap.set(year, facultyMap);
	});

	return Array.from(yearMap.entries())
		.map(([year, facultyMap]) => ({
			year,
			faculties: Array.from(facultyMap.entries())
				.map(([facultyName, majors]) => ({
					facultyName,
					majors: majors.sort((a, b) => a.majorName.localeCompare(b.majorName)),
					totalCount: majors.reduce((sum, major) => sum + major.count, 0),
				}))
				.sort((a, b) => a.facultyName.localeCompare(b.facultyName)),
		}))
		.sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
};

const buildMethodGroups = (data: SchoolStatsByIdGrouped['admissionMethodStats']): YearGroup<MethodGroupItem>[] => {
	const groups = new Map<number | null, MethodGroupItem[]>();

	data.forEach((item: SchoolStatsByIdGrouped['admissionMethodStats'][number]) => {
		const year = item.year ?? null;
		const methodName = item.tenPhuongThuc || UNKNOWN_LABEL;
		const groupItems = groups.get(year) ?? [];
		groupItems.push({
			methodName,
			count: item.count,
		});
		groups.set(year, groupItems);
	});

	return Array.from(groups.entries())
		.map(([year, items]) => ({
			year,
			items: items.sort((a, b) => a.methodName.localeCompare(b.methodName)),
		}))
		.sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
};

const isSmallScreenTableCellSx = {
	whiteSpace: 'normal',
	wordBreak: 'break-word',
};

export default function SchoolStatsPanel({ schoolId, open, selectedFaculty }: SchoolStatsPanelProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
	const [mode, setMode] = useState<StatsMode>(MODE_YEAR);

	const { data, isLoading, isError } = useQuery<SchoolStatsApiResponse>({
		queryKey: ['school', 'stats', schoolId, selectedFaculty],
		queryFn: () => schoolApi.getSchoolStatsById(schoolId, {
			maKhoa: selectedFaculty ? [selectedFaculty] : undefined,
		}),
		enabled: open && !!schoolId,
		retry: 1,
		refetchOnWindowFocus: false,
	});

	const stats = data?.data;
	const yearStats = useMemo(() => {
		const rawStats = stats?.yearStats ?? [];
		return [...rawStats].sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
	}, [stats?.yearStats]);
	const majorGroups = useMemo(() => buildMajorGroups(stats?.majorGroupStats ?? []), [stats?.majorGroupStats]);
	const methodGroups = useMemo(() => buildMethodGroups(stats?.admissionMethodStats ?? []), [stats?.admissionMethodStats]);

	const hasYearStats = yearStats.length > 0;
	const hasMajorStats = majorGroups.length > 0;
	const hasMethodStats = methodGroups.length > 0;

	const handleModeChange = (_event: React.MouseEvent<HTMLElement>, value: StatsMode | null) => {
		if (value) {
			setMode(value);
		}
	};

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
				<CircularProgress size={24} />
			</Box>
		);
	}

	if (isError) {
		return (
			<Alert severity="error">
				Không thể tải thống kê sinh viên của trường
			</Alert>
		);
	}

	if (!stats) {
		return (
			<Typography variant="body2" color="text.secondary">
				{EMPTY_TEXT}
			</Typography>
		);
	}

	return (
		<Stack gap={SECTION_GAP}>
			<Divider sx={{ my: 1 }} />
			<Typography variant="subtitle1" fontWeight="bold">
				Thống kê sinh viên
			</Typography>
			<ToggleButtonGroup
				exclusive
				value={mode}
				onChange={handleModeChange}
				size={isMobile ? 'small' : 'medium'}
				sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
			>
				<ToggleButton value={MODE_YEAR}>Theo năm</ToggleButton>
				<ToggleButton value={MODE_MAJOR}>Theo khoa/ngành</ToggleButton>
				<ToggleButton value={MODE_METHOD}>Theo phương thức</ToggleButton>
			</ToggleButtonGroup>

			{mode === MODE_YEAR && (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Năm</TableCell>
								<TableCell align="right">Số lượng</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{yearStats.length === 0 && (
								<TableRow>
									<TableCell colSpan={2}>
										<Typography variant="body2" color="text.secondary">
											{EMPTY_TEXT}
										</Typography>
									</TableCell>
								</TableRow>
							)}
							{yearStats.map((item) => (
								<TableRow key={item.year ?? 'unknown'}>
									<TableCell sx={isMobile ? isSmallScreenTableCellSx : undefined}>
										{getDisplayYear(item.year)}
									</TableCell>
									<TableCell align="right">{item.count}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{mode === MODE_MAJOR && (
				<Stack gap={SECTION_GAP}>
					{!hasMajorStats && (
						<Typography variant="body2" color="text.secondary">
							{EMPTY_TEXT}
						</Typography>
					)}
					{majorGroups.map((group) => (
						<Box key={group.year ?? 'unknown'}>
							<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
								Năm {getDisplayYear(group.year)}
							</Typography>
							<Stack gap={1.5}>
								{group.faculties.map((faculty) => (
									<Box key={`${group.year ?? 'unknown'}-${faculty.facultyName}`}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
											<Typography variant="body2" fontWeight="bold">
												{faculty.facultyName}
											</Typography>
											<Box
												sx={{
													px: FACULTY_TOTAL_PADDING_X,
													py: FACULTY_TOTAL_PADDING_Y,
													borderRadius: FACULTY_TOTAL_BORDER_RADIUS,
													border: 1,
													borderColor: FACULTY_TOTAL_BORDER_COLOR,
													bgcolor: FACULTY_TOTAL_BG_COLOR,
												}}
											>
												<Typography variant="caption" color={FACULTY_TOTAL_TEXT_COLOR} fontWeight={FACULTY_TOTAL_TEXT_WEIGHT}>
													{FACULTY_TOTAL_LABEL}: {faculty.totalCount}
												</Typography>
											</Box>
										</Box>
										<TableContainer component={Paper} variant="outlined">
											<Table size="small">
												<TableHead>
													<TableRow>
														<TableCell>Ngành</TableCell>
														<TableCell align="right">Số lượng</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{faculty.majors.map((item, index) => (
														<TableRow key={`${group.year ?? 'unknown'}-${faculty.facultyName}-${index}`}>
															<TableCell sx={isMobile ? isSmallScreenTableCellSx : undefined}>
																{item.majorName}
															</TableCell>
															<TableCell align="right">{item.count}</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									</Box>
								))}
							</Stack>
						</Box>
					))}
				</Stack>
			)}

			{mode === MODE_METHOD && (
				<Stack gap={SECTION_GAP}>
					{!hasMethodStats && (
						<Typography variant="body2" color="text.secondary">
							{EMPTY_TEXT}
						</Typography>
					)}
					{methodGroups.map((group) => (
						<Box key={group.year ?? 'unknown'}>
							<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
								Năm {getDisplayYear(group.year)}
							</Typography>
							<TableContainer component={Paper} variant="outlined">
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Phương thức</TableCell>
											<TableCell align="right">Số lượng</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{group.items.map((item, index) => (
											<TableRow key={`${group.year ?? 'unknown'}-${index}`}>
												<TableCell sx={isMobile ? isSmallScreenTableCellSx : undefined}>
													{item.methodName}
												</TableCell>
												<TableCell align="right">{item.count}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					))}
				</Stack>
			)}

			{hasYearStats || hasMajorStats || hasMethodStats ? null : (
				<Typography variant="body2" color="text.secondary">
					{EMPTY_TEXT}
				</Typography>
			)}
		</Stack>
	);
}
